import { Op } from '@unifyapps/network/generated/models/op';
import type { UIFilter } from '@unifyapps/network/generated/models/uIFilter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { setIn } from '../../../immutable';

function sanitizeMissingOrExistsOperator(filter: UIFilter): UIFilter {
  if (filter.filter?.operator === Op.MISSING || filter.filter?.operator === Op.EXISTS) {
    return setIn(filter, ['filter', 'value'], undefined);
  }
  return filter;
}

function hasValidProperty(filter: UIFilter): boolean {
  return filter.property !== undefined && filter.property !== '';
}

function hasValidDetails(filter: UIFilter): boolean {
  return !isEmpty(filter.filter?.details);
}

function hasValidNestedFilters(filter: UIFilter): boolean {
  return !!(filter.filters && filter.filters.length > 0 && filter.operator);
}

function hasValidResolvedFilter(filter: UIFilter): boolean {
  return !!(filter.resolvedFilter && !isEmpty(filter.resolvedFilter.filter));
}

function isFilterValid(filterBlock: UIFilter): boolean {
  if (filterBlock.filter) {
    return hasValidProperty(filterBlock) || hasValidDetails(filterBlock);
  }
  if (hasValidNestedFilters(filterBlock)) return true;
  if (hasValidResolvedFilter(filterBlock)) return true;
  return false;
}

function isComputedFilterValid(filterBlock: UIFilter): boolean {
  if (filterBlock.filter) {
    // this special handling because in no-code, we evaluate filters before sending them to the evaluator, where in we remove empty filters that doesn't have operator otherwise all the filters will go through evaluator
    return !!filterBlock.filter.operator;
  }
  if (hasValidNestedFilters(filterBlock)) return true;
  if (hasValidResolvedFilter(filterBlock)) return true;
  return false;
}

function getEmptyFilter(filter: UIFilter) {
  const evaluateFailureTree = get(filter, 'evaluateFailureTree', false);
  return evaluateFailureTree ? ({ evaluateFailureTree } as UIFilter) : {};
}

export function sanitizeFilters(formData: UIFilter): UIFilter {
  if (formData.filter?.operator) {
    return sanitizeMissingOrExistsOperator(formData);
  }

  const filters = formData.filters;
  if (filters && filters.length > 0) {
    const evaluateFailureTree = get(formData, 'evaluateFailureTree', false);
    const len = filters.length;
    const out = new Array(len);
    let validCount = 0;

    for (let i = 0; i < len; i++) {
      const filter = filters[i];
      if (filter) {
        const sanitized = sanitizeFilters(filter);
        if (sanitized !== undefined && isFilterValid(sanitized)) {
          out[validCount++] = sanitized;
        }
      }
    }

    if (validCount > 0) {
      const sanitizedFilters = validCount === len ? out : out.slice(0, validCount);
      return setIn(formData, ['filters'], sanitizedFilters);
    }
    return evaluateFailureTree ? ({ evaluateFailureTree } as UIFilter) : {};
  }

  return isFilterValid(formData) ? formData : getEmptyFilter(formData);
}

export function sanitizeComputedUIFilters(formData: UIFilter): UIFilter {
  if (formData.filter?.operator) {
    return sanitizeMissingOrExistsOperator(formData);
  }

  const filters = formData.filters;
  if (filters && filters.length > 0) {
    const len = filters.length;
    const out = new Array(len);
    let validCount = 0;

    for (let i = 0; i < len; i++) {
      const filter = filters[i];
      if (filter) {
        const sanitized = sanitizeComputedUIFilters(filter);
        if (isComputedFilterValid(sanitized)) {
          out[validCount++] = sanitized;
        }
      }
    }

    if (validCount > 0) {
      const sanitizedFilters = validCount === len ? out : out.slice(0, validCount);
      return setIn(formData, ['filters'], sanitizedFilters);
    }
    return {};
  }

  return isFilterValid(formData) ? formData : {};
}
